"use client"

import { useState } from "react"
import { ProfileHeader } from "./profile-header"
import { MosaicGrid } from "./mosaic-grid"
import { ProfileSkeleton } from "./profile-skeleton"
import { Button } from "#/components/ui/button"
import { Grid3X3, Bookmark, Tag } from "lucide-react"
import type { User, Post } from "#/lib/types"
import { useUsername } from "#/hooks/use-username"
import { useYourProfile } from "#/hooks/use-your-profile"
import { useOthersProfile } from "#/hooks/use-users-profile"
import { getCurrentUser } from "#/lib/mock-data"

interface ProfilePageProps {
  username: string
}

export function ProfilePage({ username }: ProfilePageProps) {
  const {
    data: currentUsername,
    isPending: isUsernamePending,
    error: currentUsernameError,
  } = useUsername()

  if (currentUsernameError) {
    return (
      <div className="text-red-500">
        Error: {JSON.stringify(currentUsernameError)}
      </div>
    )
  }

  if (isUsernamePending) {
    return <ProfileSkeleton />
  }

  const isOwnProfile = currentUsername === username

  if (isOwnProfile) {
    return <MyProfile />
  }

  return <OtherProfile username={username} />
}

function MyProfile() {
  const { data: decryptedProfile, error } = useYourProfile()

  // TODO: MOCK remove
  const currentUser = getCurrentUser()

  if (decryptedProfile) {
    return (
      <ProfileContent
        user={{ ...currentUser, bio: decryptedProfile.bio }}
        posts={[]}
        isOwnProfile
      />
    )
  }
  if (error) {
    return "An error has occurred: " + error.message
  }
  return <ProfileSkeleton />
}

function OtherProfile({ username }: { username: string }) {
  const { data: decryptedProfile, error } = useOthersProfile(username)

  // TODO: MOCK remove
  const currentUser = getCurrentUser()

  if (decryptedProfile) {
    return (
      <ProfileContent
        user={{ ...currentUser, username: username, bio: decryptedProfile.bio }}
        posts={[]}
        isOwnProfile
      />
    )
  }
  if (error) {
    return "An error has occurred: " + error.message
  }
  return <ProfileSkeleton />
}

function ProfileContent({
  user,
  posts,
  isOwnProfile = false,
}: {
  user: User
  posts: Post[]
  isOwnProfile?: boolean
}) {
  const [activeTab, setActiveTab] = useState("posts")

  const filteredPosts = posts.filter(post => {
    switch (activeTab) {
      case "posts":
        return true
      case "saved":
        return isOwnProfile // Only show saved posts for own profile
      case "tagged":
        return false // Placeholder for tagged posts
      default:
        return true
    }
  })

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 animate-in fade-in-0 slide-in-from-bottom-4">
      <ProfileHeader user={user} isOwnProfile={isOwnProfile} />

      {/* Tab Navigation */}
      <div className="border-b mb-6">
        <div className="flex justify-center gap-8">
          <Button
            variant="ghost"
            className={`flex items-center gap-2 pb-3 border-b-2 transition-colors ${
              activeTab === "posts"
                ? "border-primary text-primary"
                : "border-transparent hover:text-primary"
            }`}
            onClick={() => setActiveTab("posts")}
          >
            <Grid3X3 className="h-4 w-4" />
            <span className="hidden sm:inline">Posts</span>
          </Button>

          {isOwnProfile && (
            <Button
              variant="ghost"
              className={`flex items-center gap-2 pb-3 border-b-2 transition-colors ${
                activeTab === "saved"
                  ? "border-primary text-primary"
                  : "border-transparent hover:text-primary"
              }`}
              onClick={() => setActiveTab("saved")}
            >
              <Bookmark className="h-4 w-4" />
              <span className="hidden sm:inline">Saved</span>
            </Button>
          )}

          <Button
            variant="ghost"
            className={`flex items-center gap-2 pb-3 border-b-2 transition-colors ${
              activeTab === "tagged"
                ? "border-primary text-primary"
                : "border-transparent hover:text-primary"
            }`}
            onClick={() => setActiveTab("tagged")}
          >
            <Tag className="h-4 w-4" />
            <span className="hidden sm:inline">Tagged</span>
          </Button>
        </div>
      </div>

      {/* Content */}
      <div
        className="animate-in fade-in-0 slide-in-from-bottom-2"
        style={{ animationDelay: "200ms" }}
      >
        <MosaicGrid posts={filteredPosts} />
      </div>
    </div>
  )
}
